import React from 'react';
import { useSelector } from 'react-redux';
import { RegularButton, Typography } from 'fronton-react';
import { cx } from 'linaria';
import { YaSplitInfo } from 'types/page-model';
import { getIsBot } from 'common/selectors';
import {
  getCurrency,
  getDivisionId,
  getIsBoosterAvailable,
  getSource,
  isCalculatorAvailable,
} from 'features/product-info';
import { getIsGoodSupplyLoading, getStocksInStores } from 'features/goods-supply';
import { getLocale } from 'utils';
import {
  yandexSplitContainer,
  textContainer,
  withPrice,
  smallPadding,
  yandexSplitLogo,
  additionalMarginTop,
  additionalMarginBottom,
} from './styles';
import { formatPrice } from './utils';
import { YandexSplitBannerSkeleton } from './yandex-split-banner-skeleton';
import { createGetExperiment, getIsPdpBoosterExperiment } from 'features/page-model';
import { HIDE_CALCULATOR_INTERNAL_ID } from 'common/constants';

const xSymbol = 'х';

export type Props = {
  yaSplitInfo: YaSplitInfo;
  price?: number | null;
  currency?: string;
  isSet?: boolean;
  showCatchPushPromo?: boolean;
};

export const YandexSplitBanner: React.FC<Props> = ({
  yaSplitInfo: {
    enabled,
    mainText,
    priceBreakThreshold,
    maxPriceThreshold,
    splitCount,
    linkPage: { href, target },
    logoLink,
    KindDivisionsList,
    KindSourceList,
  },
  price,
  isSet,
  showCatchPushPromo,
}) => {
  const isBot = useSelector(getIsBot);
  const currentDivision = String(useSelector(getDivisionId));
  const currentSource = useSelector(getSource)?.toLowerCase();
  const currency = useSelector(getCurrency) || 'RUB';
  const stocks = useSelector(getStocksInStores);
  const isStocksLoading = useSelector(getIsGoodSupplyLoading);
  const isCalcHidden = useSelector(createGetExperiment(HIDE_CALCULATOR_INTERNAL_ID));
  const isCalcAvailable = (useSelector(isCalculatorAvailable) && !isCalcHidden) ?? false;
  const isShowBooster = useSelector(getIsBoosterAvailable) ?? false;
  const isPdpBoosterExperimentAvailable = useSelector(getIsPdpBoosterExperiment);
  const isBoosterAvailable = isShowBooster && isPdpBoosterExperimentAvailable;
  const isShowBoosterButton = !isCalcAvailable && isBoosterAvailable;

  if (isBot || !enabled) return null;

  if (!stocks.length && !isStocksLoading && !isSet) return null;

  if (isStocksLoading) return <YandexSplitBannerSkeleton />;

  if (price && price > maxPriceThreshold) return null;

  const isExcludedDivision = KindDivisionsList.some((d) => String(d) === currentDivision);
  const isExcludedSource = KindSourceList.some((s) => s.toLowerCase() === currentSource);

  if (isExcludedDivision || isExcludedSource) return null;

  const showPrice = price && price >= priceBreakThreshold;

  const logoSize = showPrice ? 20 : 16;

  let splitPriceFormatted;
  const locale = getLocale();

  if (showPrice) {
    const splitPrice = price / splitCount;
    splitPriceFormatted = formatPrice(splitPrice, currency, locale);
  }

  const yandexSplitContainerStyleSet = cx(yandexSplitContainer, isSet ? additionalMarginTop : null);

  const yandexSplitContainerStyle = cx(
    yandexSplitContainerStyleSet,
    (isCalcAvailable || isShowBoosterButton) && !showCatchPushPromo ? additionalMarginBottom : null,
  );

  return (
    <RegularButton
      target={target ?? '_blank'}
      href={href}
      variant="secondary"
      className={cx(yandexSplitContainerStyle, !showPrice ? smallPadding : null)}
    >
      <img className={yandexSplitLogo} src={logoLink} alt={mainText} width={logoSize} height={logoSize} />
      <div className={showPrice ? cx(textContainer, withPrice) : textContainer}>
        {showPrice && (
          <Typography color="text-primary" size="body_accent" variant="s">
            {splitPriceFormatted} {xSymbol} {splitCount}
          </Typography>
        )}
        <Typography color="text-primary" size="caption" variant="minor">
          {mainText}
        </Typography>
      </div>
    </RegularButton>
  );
};
